/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAiScanTargetsRequest,
  ListAiScanTargetsSecurity,
} from "../models/operations/listaiscantargets.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AiScanTargetsQueryData,
  buildAiScanTargetsQuery,
  prefetchAiScanTargets,
  queryKeyAiScanTargets,
} from "./aiScanTargets.core.js";
export {
  type AiScanTargetsQueryData,
  buildAiScanTargetsQuery,
  prefetchAiScanTargets,
  queryKeyAiScanTargets,
};

export type AiScanTargetsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listAiScanTargets agent
 *
 * @remarks
 * List the Shadow AI scan targets this organization's device agents probe for: the Speakeasy built-ins plus the organization's own additions, with the catalog version agents echo on scan receipts. Everything listed is probed for; a built-in leaves the list by leaving Speakeasy's catalog, an organization target by being deleted. Requires a session with the org:admin scope.
 */
export function useAiScanTargets(
  request?: ListAiScanTargetsRequest | undefined,
  security?: ListAiScanTargetsSecurity | undefined,
  options?: QueryHookOptions<AiScanTargetsQueryData, AiScanTargetsQueryError>,
): UseQueryResult<AiScanTargetsQueryData, AiScanTargetsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAiScanTargetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listAiScanTargets agent
 *
 * @remarks
 * List the Shadow AI scan targets this organization's device agents probe for: the Speakeasy built-ins plus the organization's own additions, with the catalog version agents echo on scan receipts. Everything listed is probed for; a built-in leaves the list by leaving Speakeasy's catalog, an organization target by being deleted. Requires a session with the org:admin scope.
 */
export function useAiScanTargetsSuspense(
  request?: ListAiScanTargetsRequest | undefined,
  security?: ListAiScanTargetsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AiScanTargetsQueryData,
    AiScanTargetsQueryError
  >,
): UseSuspenseQueryResult<AiScanTargetsQueryData, AiScanTargetsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAiScanTargetsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAiScanTargetsData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: AiScanTargetsQueryData,
): AiScanTargetsQueryData | undefined {
  const key = queryKeyAiScanTargets(...queryKeyBase);

  return client.setQueryData<AiScanTargetsQueryData>(key, data);
}

export function invalidateAiScanTargets(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "agent", "listAiScanTargets", ...queryKeyBase],
  });
}

export function invalidateAllAiScanTargets(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "agent", "listAiScanTargets"],
  });
}
